/**
 * Messaging composable for the /messaging namespace.
 *
 * initMessaging() — call once from App.vue to register event listeners and connect.
 * useMessaging()  — returns action functions for components that need to send/manage messages.
 */
import { ref } from 'vue'
import { getNamespaceSocket } from './useSocket'
import { useMessagingStore } from '@/stores/messaging.store'

let initialized = false
const isConnected = ref(false)

/**
 * Connect the /messaging namespace and register event listeners.
 * Safe to call multiple times — only initializes once.
 */
export function initMessaging() {
  if (initialized) return

  const socket = getNamespaceSocket('/messaging')
  if (!socket) return

  initialized = true
  const messagingStore = useMessagingStore()

  socket.on('connect', () => {
    isConnected.value = true
  })

  socket.on('disconnect', () => {
    isConnected.value = false
  })

  socket.on('new_message', payload => {
    messagingStore.handleNewMessage(payload)
  })

  socket.on('message_deleted', payload => {
    messagingStore.handleMessageDeleted(payload)
  })

  socket.on('messaging_error', payload => {
    console.error('Messaging socket error', payload.error)
  })

  socket.on('connect_error', err => {
    console.error('Messaging socket connect error', err.message)
  })

  socket.connect()
}

/**
 * Reset initialization state (called on logout so next login re-initializes).
 */
export function resetMessaging() {
  initialized = false
  isConnected.value = false
}

/**
 * Returns action functions for messaging. Does NOT manage connection lifecycle.
 */
export function useMessaging() {
  function sendMessageViaSocket(conversationId, text, reservationId = null) {
    return new Promise((resolve, reject) => {
      const socket = getNamespaceSocket('/messaging')
      if (!socket?.connected) {
        reject(new Error('Not connected'))

        return
      }
      socket.emit('send_message', { conversationId, text, reservationId }, response => {
        if (response.error) reject(new Error(response.error))
        else resolve(response.message)
      })
    })
  }

  function sendRaMessageViaSocket(conversationId, text) {
    return new Promise((resolve, reject) => {
      const socket = getNamespaceSocket('/messaging')
      if (!socket?.connected) {
        reject(new Error('Not connected'))

        return
      }
      socket.emit('ra_send_message', { conversationId, text }, response => {
        if (response.error) reject(new Error(response.error))
        else resolve(response.message)
      })
    })
  }

  function viewConversation(conversationId) {
    const socket = getNamespaceSocket('/messaging')
    if (socket?.connected) {
      socket.emit('viewing_conversation', { conversationId })
    }
  }

  function leaveConversation() {
    const socket = getNamespaceSocket('/messaging')
    if (socket?.connected) {
      socket.emit('leave_conversation')
    }
  }

  function deleteMessageViaSocket(messageId) {
    return new Promise((resolve, reject) => {
      const socket = getNamespaceSocket('/messaging')
      if (!socket?.connected) {
        reject(new Error('Not connected'))

        return
      }
      socket.emit('delete_message', { messageId }, response => {
        if (response && response.error) reject(new Error(response.error))
        else resolve()
      })
    })
  }

  return {
    isConnected,
    sendMessageViaSocket,
    sendRaMessageViaSocket,
    deleteMessageViaSocket,
    viewConversation,
    leaveConversation,
  }
}
